import type {
  Section,
  Skill,
  Project,
  Experience,
  SectionKey,
  AboutDetail,
  Education,
} from "./types";

export const sections: Record<SectionKey, Section> = {
  home: { label: "Home", icon: "⌂" },
  about: { label: "About", icon: "◉" },
  skills: { label: "Skills", icon: "◈" },
  experience: { label: "Experience", icon: "▸" },
  projects: { label: "Projects", icon: "□" },
  education: { label: "Education", icon: "◇" },
  resume: { label: "Resume", icon: "↓" },
};

export const skills: Record<string, Skill[]> = {
  Frontend: [
    { name: "Vue.js", level: "expert" },
    { name: "React", level: "advanced" },
    { name: "TypeScript", level: "advanced" },
    { name: "Tailwind CSS", level: "expert" },
    { name: "HTML5 / CSS3", level: "expert" },
  ],
  Backend: [
    { name: "Python", level: "expert" },
    { name: "Django", level: "expert" },
    { name: "REST APIs", level: "expert" },
    { name: "GraphQL", level: "intermediate" },
  ],
  Database: [
    { name: "PostgreSQL", level: "advanced" },
    { name: "MongoDB", level: "advanced" },
    { name: "MySQL", level: "advanced" },
  ],
  "DevOps & Security": [
    { name: "Docker", level: "advanced" },
    { name: "CI/CD", level: "advanced" },
    { name: "AWS", level: "intermediate" },
    { name: "App Security", level: "advanced" },
  ],
};

export const projects: Project[] = [
  {
    title: "SmartCampus Platform",
    tech: ["React", "Django", "PostgreSQL"],
    desc: "Multi-tenant academic operations platform managing students, staff, classes, grading, attendance, payments, and analytics across 13 schools.",
    link: "https://kasms.vercel.app",
  },
  {
    title: "Interstate Truck Drivers Platform",
    tech: ["Django", "Python"],
    desc: "Route and operations management system with trip planning, optimal route computation, and driver activity logging.",
    link: "https://truckops.ericmwangi.dev",
  },
  {
    title: "Operations Management Platform",
    tech: ["Vue.js", "Django", "PostgreSQL"],
    desc: "Personnel and operations management with role-based access control, task tracking, and reporting dashboards.",
    link: "https://github.com/Ericmuturimwangi/Unit-Management-System",
  },
  {
    title: "Job Portfolio Platform",
    tech: ["Django", "Bootstrap"],
    desc: "Real-time job listing and application platform with advanced search, filtering, and applicant tracking.",
    link: null,
  },
];

export const experience: Experience[] = [
  {
    role: "Software Developer",
    company: "theDifference Developers",
    period: "2024 – Present",
    current: true,
    points: [
      "Designed a scalable, multi-tenant school management platform automating operations across 13 independent schools.",
      "Engineered RESTful APIs for a unified ecosystem supporting web dashboards, mobile access, and upcoming portals.",
      "Built centralized communication features for automated announcements, alerts, and reminders via email and SMS.",
    ],
  },
  {
    role: "Software Developer",
    company: "Freelance",
    period: "2022 – 2024",
    current: false,
    points: [
      "Built an ecommerce platform with Django, with modular cart, payment, product and store architecture.",
      "Developed an interstate truck driver platform computing optimal route instructions from trip details.",
      "Created an employee management system handling personnel, roles, teams, and reporting structures.",
    ],
  },
  {
    role: "Intern Software Developer",
    company: "MindTech Solutions",
    period: "2021 – 2022",
    current: false,
    points: [
      "Deployed and managed scalable applications across AWS, ensuring high availability.",
      "Implemented serverless functions with AWS Lambda and automated infrastructure with CloudFormation.",
      "Managed databases with Amazon RDS and OS virtualization for secure computing environments.",
    ],
  },
];

export const aboutInfo: { bio: string; details: AboutDetail[] } = {
  bio: "I'm a passionate developer with a love for creating elegant solutions to complex problems. I focus on software development and application security, building scalable systems that make a real impact.",
  details: [
    { label: "Location", value: "Kenya 🇰🇪" },
    { label: "Focus", value: "Full Stack + Security" },
    {
      label: "Email",
      value: "ericmwangi837@gmail.com",
      href: "mailto:ericmwangi837@gmail.com",
    },
    {
      label: "GitHub",
      value: "Ericmuturimwangi",
      href: "https://github.com/Ericmuturimwangi",
    },
  ],
};

export const education: Education = {
  degree: "BSc Computer Science",
  school: "Chuka University",
  period: "2017 – 2021",
  coursework: [
    "Data Structures",
    "Web Development",
    "Database Management",
    "Software Engineering",
    "Application Security",
  ],
  certifications: [
    {
      name: "Africahackon Cybersecurity",
      href: "https://www.credly.com/badges/f9d18d5f-0613-47eb-aa77-843100ea5628/linked_in_profile",
    },
    {
      name: "Huawei Global Training Center — Networking & Telecom (2025)",
      href: null,
    },
  ],
};

export const links = {
  github: "https://github.com/Ericmuturimwangi",
  email: "mailto:ericmwangi837@gmail.com",
  resume: "/Eric_Mwangi_Resume.pdf",
};